<script setup lang="ts">
import {
    collectCollapsibleMenuTitles,
    filterMenuByPermissions,
    markMenuItemsActive,
    ordenarMenuExibido,
    menuItems,
    processMenuLinks,
    type MenuItem
} from '@/config/menu';
import { useCustomizerStore } from '@/stores/customizer';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import api from '@/config/axios';
import { UI_VERSION } from '@/config/version';
import { useAuthStore } from '@/stores/authStore';
import { useReembolsoAprovadorFlag } from '@/composables/useReembolsoAprovadorFlag';
import { useReembolsoConferenteFlag } from '@/composables/useReembolsoConferenteFlag';
import { useReembolsoCentralFlag } from '@/composables/useReembolsoCentralFlag';
import { useCartaoGestorFlag } from '@/composables/useCartaoGestorFlag';
import { useInovacaoAvaliadorFlag } from '@/composables/useInovacaoAvaliadorFlag';
import Logo from '../logo/Logo.vue';
import NavCollapse from './NavCollapse/NavCollapse.vue';
import NavGroup from './NavGroup/index.vue';
import NavItem from './NavItem/index.vue';

const customizer = useCustomizerStore();
const authStore = useAuthStore();
const route = useRoute();
const permissionsLoaded = ref(false);
const { ehAprovador: ehAprovadorReembolso } = useReembolsoAprovadorFlag();
const { isConferente: ehConferenteReembolso } = useReembolsoConferenteFlag();
const { podeVer: podeVerCentralFinanceiro } = useReembolsoCentralFlag();
const { ehGestor: ehGestorCartao } = useCartaoGestorFlag();
const { isAvaliador: ehAvaliadorInovacao } = useInovacaoAvaliadorFlag();

/**
 * Filtra recursivamente itens marcados por papel dinâmico (`requiresAprovador`
 * de reembolso, `requiresGestor` de cartão). Mantém itens enquanto a flag é
 * `null` (ainda carregando) para evitar flicker no menu.
 */
function filterAprovadorOnlyItems(items: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = [];
  for (const item of items) {
    if (item.requiresAprovador && ehAprovadorReembolso.value === false) {
      continue;
    }
    if (item.requiresConferente && ehConferenteReembolso.value === false) {
      continue;
    }
    if (item.requiresCentralFinanceiro && podeVerCentralFinanceiro.value === false) {
      continue;
    }
    if (item.requiresGestor && ehGestorCartao.value === false) {
      continue;
    }
    if (item.requiresAvaliadorInovacao && ehAvaliadorInovacao.value === false) {
      continue;
    }
    if (item.children && item.children.length > 0) {
      const filteredChildren = filterAprovadorOnlyItems(item.children);
      if (
        filteredChildren.length === 0 &&
        !item.header &&
        (!item.to || item.to === '#')
      ) {
        // container vazio: oculta
        continue;
      }
      result.push({ ...item, children: filteredChildren });
    } else {
      result.push(item);
    }
  }
  return result;
}

/** Estado aberto/fechado dos `v-list-group` (Vuetify 3). */
const menuOpened = ref<(string | number)[]>(readSidebarOpenedFromLocalStorage());

function readSidebarOpenedFromLocalStorage(): string[] {
    const out: string[] = [];
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k?.startsWith('sidebar_menu_')) continue;
            if (localStorage.getItem(k) === 'true') {
                out.push(k.slice('sidebar_menu_'.length));
            }
        }
    } catch {
        /* ignore */
    }
    return out;
}

// Links úteis carregados da API
const { t } = useI18n();
const menuLinksChildren = ref<MenuItem[]>([]);

// Versão da UI do release-please manifest
const uiVersion = computed(() => UI_VERSION);

// Carregar links úteis da API
const loadMenuLinks = async () => {
    try {
        const response = await api.get('/menu-links');
        const linksData = response.data.data;
        // Transformar em estrutura de children do menu
        menuLinksChildren.value = Object.entries(linksData).map(([category, links]) => ({
            title: category,
            icon: 'folder-line-duotone',
            BgColor: 'primary',
            to: '#',
            children: (links as any[]).map((link) => ({
                title: link.link_name,
                to: link.link_url,
                type: 'external' as const,
                BgColor: 'primary'
            }))
        }));
    } catch (error) {
        console.error('Erro ao carregar links úteis:', error);
        menuLinksChildren.value = [];
    }
};

// Carregar permissões ao montar o componente
onMounted(async () => {
    // Garantir que permissões do usuário estão carregadas (userAccessMap)
    await authStore.fetchUserRouteLevelPaths();
    await loadMenuLinks();
    permissionsLoaded.value = true;
});

const isAdminMaster = computed(() => authStore.isAdminMaster);

const sidebarMenu = computed(() => {
    // Aguardar carregamento das permissões antes de filtrar
    if (!permissionsLoaded.value) {
        return [];
    }

    // Processar links dinâmicos do menu (ex: @profile?tab=kanban)
    const samaccountname = authStore.user?.sAMAccountName || authStore.user?.username;
    const processedMenu = processMenuLinks(menuItems, samaccountname);

    // Injetar children dinâmicos no item "Links Úteis"
    const menuWithDynamicLinks = processedMenu.map((item) => {
        if (item.title === 'Links Úteis' && menuLinksChildren.value.length > 0) {
            return {
                ...item,
                children: menuLinksChildren.value
            };
        }
        return item;
    });

    // Filtrar menu por permissões (fonte única: userAccessMap do banco de dados)
    const filteredMenu = filterMenuByPermissions(
        ordenarMenuExibido(menuWithDynamicLinks, (x) => t(x)),
        isAdminMaster.value,
        authStore.userGroups,
        authStore.user?.department || '',
        authStore.user?.company || '',
        undefined,
        undefined,
        authStore.userRouteLevelPaths
    );

    // Filtrar itens marcados como requiresAprovador (ex.: Aprovações de Reembolso)
    const aprovadorFilteredMenu = filterAprovadorOnlyItems(filteredMenu);

    // Marcar itens como ativos baseado na rota atual (usar fullPath para incluir query params)
    return markMenuItemsActive(aprovadorFilteredMenu, route.fullPath);
});

function persistSidebarMenuOpened(opened: readonly (string | number)[]) {
    try {
        const set = new Set(opened.map(String));
        for (const title of collectCollapsibleMenuTitles(sidebarMenu.value)) {
            localStorage.setItem(`sidebar_menu_${title}`, set.has(title) ? 'true' : 'false');
        }
    } catch {
        /* ignore */
    }
}

watch(
    sidebarMenu,
    (menu) => {
        if (!menu.length) return;
        const valid = new Set(collectCollapsibleMenuTitles(menu));
        const next = menuOpened.value.filter((v) => valid.has(String(v)));
        if (next.length !== menuOpened.value.length) {
            menuOpened.value = next;
        }
    },
    { flush: 'post' }
);

// ✅ Detectar se está em modo escuro
const isDark = computed(() => {
    return (
        customizer.actTheme === 'DARK_BLUE_THEME' ||
        customizer.actTheme === 'DARK_AQUA_THEME' ||
        customizer.actTheme === 'DARK_ORANGE_THEME' ||
        customizer.actTheme === 'DARK_PURPLE_THEME' ||
        customizer.actTheme === 'DARK_GREEN_THEME' ||
        customizer.actTheme === 'DARK_CYAN_THEME'
    );
});

// ✅ Cor do texto baseado no tema
const textColor = computed(() => {
    return isDark.value ? 'white' : '#2A3547';
});
</script>

<template>
    <v-navigation-drawer
        left
        v-model="customizer.Sidebar_drawer"
        rail-width="70"
        app
        class="leftSidebar ms-lg-5 mt-sm-5 bg-containerBg"
        elevation="10"
        :rail="customizer.mini_sidebar"
        expand-on-hover
        width="270"
    >
        <div class="pa-5 pl-4">
            <Logo />
        </div>

        <!-- Navigation -->
        <perfect-scrollbar class="scrollnavbar bg-containerBg overflow-y-hidden">
            <v-list class="py-4 px-4 bg-containerBg" v-model:opened="menuOpened" @update:opened="persistSidebarMenuOpened">
                <!---Menu Loop -->
                <template v-for="(item, i) in sidebarMenu" :key="i">
                    <!---Item Sub Header -->
                    <NavGroup :item="item" v-if="item.header" />
                    <!---If Has Child -->
                    <NavCollapse class="leftPadding" :item="item" :level="0" v-else-if="item.children" />
                    <!---Single Item-->
                    <NavItem :item="item" v-else class="leftPadding" />
                    <!---End Single Item-->
                </template>
            </v-list>

            <!-- Versionamento oculto
      <div class="pa-6 w-full text-center userbottom bg-containerBg mt-2">
        <div class="d-flex align-center justify-center">
          <span class="text-caption" :style="{ color: textColor, opacity: 0.5, fontSize: '8px' }">
            Version: {{ uiVersion }}
          </span>
        </div>
      </div>
      -->
        </perfect-scrollbar>
    </v-navigation-drawer>
</template>
